import { Card, CardContent, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Query } from '@/components/atoms/query';
import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { useDestinations } from '@/features/destinations/hooks/query/use-destinations';
import { useDetachDestinationsFromHost } from '@/features/hosts/hooks/mutation/use-detach-destinations-from-host';
import { useUpdateHostDestinations } from '@/features/hosts/hooks/mutation/use-update-host-destinations';

interface Props extends DialogActions {
  hostId: number;
  destinationsIds: number[];
}

export const UpdateHostDestinationsDialog = ({
  onCancel,
  open,
  hostId,
  destinationsIds,
}: Props) => {
  const { t } = useTranslation(['host']);

  const { mutateAsync: detachDestinationsFromHost } = useDetachDestinationsFromHost(onCancel);

  const { mutateAsync: updateHostDestinations } = useUpdateHostDestinations(onCancel);

  const [selectedDestinationsIds, setSelectedDestinationsIds] = useState<number[]>(
    destinationsIds || [],
  );

  const destinationsIdsToRemove =
    destinationsIds?.filter(destinationId => !selectedDestinationsIds.includes(destinationId)) ??
    [];

  // WE ARE FETCHING ONLY ONE DESTINATION HERE TO GET TOTAL NUMBER OF DESTINATIONS
  const { data: destinationData } = useDestinations({ page: 1, perPage: 1 });

  const { data } = useDestinations({
    page: 1,
    perPage: destinationData?.total || 0,
    enabled: destinationData && destinationData?.total > 0,
  });

  const handleSelectDestinations = (id: number) => {
    const copyPickedDestinationsIds = selectedDestinationsIds.slice();

    if (copyPickedDestinationsIds.findIndex(el => el === id) > -1) {
      copyPickedDestinationsIds.splice(
        copyPickedDestinationsIds.findIndex(el => el === id),
        1,
      );
    } else {
      copyPickedDestinationsIds.push(id);
    }

    setSelectedDestinationsIds(copyPickedDestinationsIds);
  };

  const [query, setQuery] = useState('');

  useEffect(() => {
    setSelectedDestinationsIds(destinationsIds || []);
  }, [destinationsIds]);

  const onConfirm = async () => {
    await updateHostDestinations({ hostId, destinationsIds: selectedDestinationsIds });
    if (destinationsIdsToRemove.length > 0) {
      await detachDestinationsFromHost({
        hostId,
        destinationsIds: destinationsIdsToRemove,
      });
    }
  };

  return (
    <Dialog
      variant="info"
      title={t('host:discount.chooseDiscounts')}
      onCancel={onCancel}
      open={open}
      onConfirm={onConfirm}>
      <Query disabled={false} value={query} onChange={value => setQuery(value)} searchAsType />
      <Box sx={{ my: 1 }}>
        {data?.resources
          ?.filter(dest => dest.name.toLowerCase().includes(query.toLowerCase()))
          ?.map(destination => {
            const { name, id } = destination;

            return (
              <Card
                onClick={() => handleSelectDestinations(id)}
                key={id}
                sx={{
                  boxShadow: theme =>
                    selectedDestinationsIds.includes(id)
                      ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                      : `0px 0px 0px 1px ${theme.palette.divider}`,
                  cursor: 'pointer',
                  mb: 1,
                }}>
                <CardContent
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  <Box>
                    <Typography color="textPrimary" variant="h6">
                      {name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
      </Box>
    </Dialog>
  );
};
